"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { useI18n } from "@/lib/i18n/context"
import { CONSENTIMIENTO_CAMBIADO, esPaginaDeAcceso, estoyEnLaApp } from "@/lib/comun/en-la-app"
import { usePathname } from "next/navigation"

const CONSENT_KEY = "cookie_consent_v1"

/**
 * Dentro de las apps de Zindra (escritorio, iOS y Android) no se pregunta por
 * las cookies: ver `estoyEnLaApp`.
 *
 * **No es una excepcion legal, es que la pregunta no viene a cuento.** La
 * aplicacion de escritorio no es una visita a un sitio web: la persona ya ha
 * entrado con su cuenta y las unicas cookies que hay ahi son las de su sesion,
 * que son las necesarias y no se consienten. Ensenarle un banner de cookies
 * dentro de un programa que se ha instalado es pedirle permiso para lo que no
 * necesita permiso, y encima tapando el chat.
 *
 * La marca la pone el propio programa en su agente de usuario, sobre una
 * particion que solo el usa.
 */

type ConsentPrefs = {
  necessary: boolean
  preferences: boolean
  analytics: boolean
  marketing: boolean
}

export default function CookieConsentBanner() {
  const { t } = useI18n()
  const [visible, setVisible] = useState(false)
  const [configOpen, setConfigOpen] = useState(false)
  const [prefs, setPrefs] = useState<ConsentPrefs>({ necessary: true, preferences: false, analytics: false, marketing: false })
  const ruta = usePathname() ?? ""

  useEffect(() => {
    // Ni el escritorio ni el móvil: dentro de las apps no se rastrea, y un
    // aviso de cookies que ofrece «marketing» es justo lo que App Store lee
    // como rastreo sin App Tracking Transparency.
    if (estoyEnLaApp() || esPaginaDeAcceso(ruta)) {
      setVisible(false)
      return
    }

    try {
      const stored = localStorage.getItem(CONSENT_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        if (parsed && typeof parsed === 'object') {
          setPrefs({
            necessary: true,
            preferences: !!parsed.preferences,
            analytics: !!parsed.analytics,
            marketing: !!parsed.marketing,
          })
        }
      } else {
        setVisible(true)
      }
    } catch {
      setVisible(true)
    }
  }, [ruta])

  function persist(newPrefs: ConsentPrefs) {
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify({ ...newPrefs, ts: Date.now() }))
      const value = encodeURIComponent(
        JSON.stringify({ p: newPrefs.preferences ? 1 : 0, a: newPrefs.analytics ? 1 : 0, m: newPrefs.marketing ? 1 : 0 })
      )
      document.cookie = `${CONSENT_KEY}=${value}; path=/; max-age=${60 * 60 * 24 * 365}`
      // La analítica espera a esto para cargarse (ver google-analytics.tsx).
      window.dispatchEvent(new Event(CONSENTIMIENTO_CAMBIADO))
    } catch {}
  }

  function acceptAll() {
    // Sin publicidad: no hay nada de «marketing» que aceptar.
    const newPrefs = { necessary: true, preferences: true, analytics: true, marketing: false }
    setPrefs(newPrefs)
    persist(newPrefs)
    setVisible(false)
  }

  function rejectAll() {
    const newPrefs = { necessary: true, preferences: false, analytics: false, marketing: false }
    setPrefs(newPrefs)
    persist(newPrefs)
    setVisible(false)
  }

  function saveConfig() {
    persist(prefs)
    setVisible(false)
    setConfigOpen(false)
  }

  if (!visible) return null

  return (
    <div className="fixed inset-x-0 bottom-0 z-50">
      <div className="mx-auto max-w-5xl p-4">
        <div className="rounded-lg border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 p-4 shadow-lg">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              {t('legal.banner.message')}
              <Link href="/legal/cookies" className="underline">{t('legal.banner.policy')}</Link>.
            </p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={rejectAll}>{t('legal.banner.reject')}</Button>
              <Button size="sm" variant="secondary" onClick={() => setConfigOpen((v) => !v)}>{t('legal.banner.configure')}</Button>
              <Button size="sm" variant="default" onClick={acceptAll}>{t('legal.banner.accept')}</Button>
            </div>
          </div>

          {configOpen && (
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <div className="rounded-md border p-3">
                <div className="flex items-start gap-3">
                  <Checkbox checked disabled className="mt-1" />
                  <div>
                    <div className="text-sm font-medium">{t('legal.banner.categories.necessary')}</div>
                    <div className="text-xs text-muted-foreground">{t('legal.banner.descriptions.necessary')}</div>
                  </div>
                </div>
              </div>
              <div className="rounded-md border p-3">
                <div className="flex items-start gap-3">
                  <Checkbox
                    checked={prefs.preferences}
                    onCheckedChange={(v) => setPrefs((p) => ({ ...p, preferences: !!v }))}
                    className="mt-1"
                  />
                  <div>
                    <div className="text-sm font-medium">{t('legal.banner.categories.preferences')}</div>
                    <div className="text-xs text-muted-foreground">{t('legal.banner.descriptions.preferences')}</div>
                  </div>
                </div>
              </div>
              <div className="rounded-md border p-3">
                <div className="flex items-start gap-3">
                  <Checkbox
                    checked={prefs.analytics}
                    onCheckedChange={(v) => setPrefs((p) => ({ ...p, analytics: !!v }))}
                    className="mt-1"
                  />
                  <div>
                    <div className="text-sm font-medium">{t('legal.banner.categories.analytics')}</div>
                    <div className="text-xs text-muted-foreground">{t('legal.banner.descriptions.analytics')}</div>
                  </div>
                </div>
              </div>
              <div className="sm:col-span-3 flex justify-end gap-2">
                <Button size="sm" variant="secondary" onClick={() => setConfigOpen(false)}>{t('common.cancel')}</Button>
                <Button size="sm" variant="default" onClick={saveConfig}>{t('legal.banner.save')}</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
